<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import AppCard from './ui/AppCard.vue'
import { useLotteryStore } from '../stores/lottery.js'
import TicketBatchModal from './TicketBatchModal.vue'

const props = defineProps({
  assignments: {
    type: Array,
    default: () => [],
  },
  lotteryId: {
    type: [String, Number],
    required: true,
  },
})

const lotteryStore = useLotteryStore()
const selectedBatch = ref(null)
const selectedUsername = ref('')
const modalLoading = ref(false)
const modalError = ref('')
const isMobile = ref(false)
let mobileResizeHandler = null

const assignmentIdentityLabel = (assignment) => {
  const displayName = String(assignment?.display_name ?? assignment?.username ?? '').trim()
  const phone = String(assignment?.display_phone ?? '').trim()
  const userId = Number(assignment?.user_id)
  const fallback = Number.isInteger(userId) && userId > 0 ? `User #${userId}` : 'Unknown user'
  const base = displayName || fallback
  return phone ? `${base} (${phone})` : base
}

const syncIsMobile = () => {
  if (typeof window === 'undefined') return
  isMobile.value = window.innerWidth <= 768
}

onMounted(() => {
  syncIsMobile()
  mobileResizeHandler = () => syncIsMobile()
  window.addEventListener('resize', mobileResizeHandler)
})

onBeforeUnmount(() => {
  if (mobileResizeHandler) window.removeEventListener('resize', mobileResizeHandler)
})

const openUserBatch = async (assignment) => {
  if (!assignment.has_uploaded) return
  modalLoading.value = true
  modalError.value = ''
  selectedBatch.value = null
  selectedUsername.value = assignmentIdentityLabel(assignment)

  const result = await lotteryStore.fetchUserBatches(props.lotteryId, assignment.user_id)
  if (result.success && result.data.length > 0) {
    selectedBatch.value = result.data[result.data.length - 1]
  } else {
    modalError.value = result.error ?? 'Ingen opplastinger funnet.'
  }
  modalLoading.value = false
}

const closeModal = () => {
  selectedBatch.value = null
  modalError.value = ''
  selectedUsername.value = ''
}
</script>

<template>
  <div class="assignment-table-wrapper">
    <div v-if="isMobile && assignments.length > 0" class="assignment-card-list">
      <AppCard
        v-for="assignment in assignments"
        :key="assignment.assigned_index"
        class="mobile-card assignment-card"
        :class="{ clickable: assignment.has_uploaded }"
        :title="assignment.has_uploaded ? 'Klikk for å se loddark' : ''"
        @click="openUserBatch(assignment)"
      >
        <div class="mobile-card-title-wrap">
          <div class="mobile-card-title">{{ assignmentIdentityLabel(assignment) }}</div>
          <div class="mobile-card-subtitle">Index #{{ assignment.assigned_index }}</div>
        </div>

        <div class="mobile-card-meta">
          <div><strong>Uploaded:</strong> <span :class="{ uploaded: assignment.has_uploaded }">{{ assignment.has_uploaded ? 'Yes' : 'No' }}</span></div>
          <div><strong>Tickets:</strong> {{ assignment.number_of_tickets }}</div>
          <div><strong>Global Range:</strong> {{ assignment.global_start }} - {{ assignment.global_end }}</div>
        </div>

        <div v-if="assignment.has_uploaded" class="mobile-card-actions">
          <button type="button" class="btn btn-primary" @click.stop="openUserBatch(assignment)">View upload</button>
        </div>
      </AppCard>
    </div>

    <table v-else-if="assignments.length > 0" class="table table-wrapper">
      <thead>
        <tr>
          <th>Username</th>
          <th>Index</th>
          <th>Uploaded</th>
          <th>Tickets</th>
          <th>Global Range</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="assignment in assignments"
          :key="assignment.assigned_index"
          :class="{ clickable: assignment.has_uploaded }"
          :title="assignment.has_uploaded ? 'Klikk for å se loddark' : ''"
          @click="openUserBatch(assignment)"
        >
          <td class="username-cell">
            {{ assignmentIdentityLabel(assignment) }}
            <span v-if="assignment.has_uploaded" class="view-hint">🖼</span>
          </td>
          <td>{{ assignment.assigned_index }}</td>
          <td :class="{ uploaded: assignment.has_uploaded }">
            {{ assignment.has_uploaded ? '✓ Yes' : '✗ No' }}
          </td>
          <td>{{ assignment.number_of_tickets }}</td>
          <td>{{ assignment.global_start }} - {{ assignment.global_end }}</td>
        </tr>
      </tbody>
    </table>

    <div v-else class="empty">No assignments yet</div>

    <div v-if="modalLoading" class="modal-overlay-simple">
      <div class="loading-box">Henter loddark...</div>
    </div>

    <div v-if="modalError" class="fetch-error">{{ modalError }}</div>

    <TicketBatchModal
      v-if="selectedBatch"
      :batch="selectedBatch"
      :username="selectedUsername"
      :title="`Loddark — ${selectedUsername}`"
      @close="closeModal"
    />
  </div>
</template>

<style scoped>
.assignment-table-wrapper {
  width: 100%;
}

.table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
}

.table-wrapper {
  overflow-x: auto;
}

.table th {
  background-color: #f5f5f5;
  color: #111827;
  padding: 1rem;
  text-align: left;
  font-weight: bold;
  border-bottom: 2px solid #ddd;
}

.table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  color: #111827;
}

.table tr:hover {
  background-color: #f9f9f9;
}

.table tr.clickable {
  cursor: pointer;
}

.table tr.clickable:hover {
  background-color: #eff6ff;
}

.assignment-card-list {
  display: grid;
  gap: 0.75rem;
}

.mobile-card-title-wrap {
  display: grid;
  gap: 0.2rem;
}

.mobile-card-title {
  font-weight: 700;
  color: #111827;
}

.mobile-card-subtitle {
  color: #6b7280;
  font-size: 0.9rem;
}

.mobile-card-meta {
  display: grid;
  gap: 0.35rem;
  color: #111827;
  font-size: 0.95rem;
}

.mobile-card-meta strong {
  color: #374151;
}

.mobile-card-actions {
  display: flex;
  margin-top: 0.5rem;
}

.username-cell {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.view-hint {
  font-size: 0.85rem;
  opacity: 0.7;
}

.uploaded {
  color: #4caf50;
  font-weight: bold;
}

.empty {
  padding: 2rem;
  text-align: center;
  color: #999;
  background-color: white;
}

.modal-overlay-simple {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.loading-box {
  background: white;
  padding: 2rem 3rem;
  border-radius: 8px;
  font-weight: bold;
  color: #2196f3;
}

.fetch-error {
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 6px;
  font-size: 0.875rem;
}

.assignment-card {
  display: grid;
  gap: 8px;
}

@media (max-width: 768px) {
  .table-wrapper {
    overflow-x: auto;
  }
}
</style>

